import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { CartLineModel, CartModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { isMgbwWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import QtyMinusIcon from "@tSurya/Icons/QtyMinusIcon";
import QtyPlusIcon from "@tSurya/Icons/QtyPlusIcon";
import { TYPOGRAPHY } from "@tSurya/theme/design.surya";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    cartLine: CartLineModel;
    cart: CartModel;
    canModifyOrder: boolean;
    isLoading: boolean;
    cartLoadedTime: number;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CustomQuantityInputStyles;
    label?: string;
}

export interface CustomQuantityInputStyles {
    qtyContainer?: InjectableCss;
    quantityInput?: InjectableCss;
    decreaseButtonStyle?: InjectableCss;
    increaseButtonStyle?: InjectableCss;
    modalContainer?: ModalPresentationProps;
}

export const customQuantityInputStyles: CustomQuantityInputStyles = {
    qtyContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: fit-content;
            height: 44px;
            border: 1px solid #e0e0e0;
            border-radius: 4px;
            background: #ffffff;
            overflow: hidden;

            /* Hide the original label from TextField */
            label {
                display: none !important;
            }
        `,
    },
    decreaseButtonStyle: {
        css: css`
            background: transparent;
            border: none;
            padding: 0;
            width: 44px;
            height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;

            &:hover {
                background: rgba(0, 0, 0, 0.1);
            }

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;

                &:hover {
                    background: transparent;
                }
            }

            svg {
                width: 14px;
                height: 14px;
                color: ${getColor("text.main")};
            }
        `,
    },
    increaseButtonStyle: {
        css: css`
            background: transparent;
            border: none;
            padding: 0;
            width: 44px;
            height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;

            &:hover {
                background: rgba(0, 0, 0, 0.1);
            }

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;

                &:hover {
                    background: transparent;
                }
            }

            svg {
                width: 14px;
                height: 14px;
                color: ${getColor("text.main")};
            }
        `,
    },
    quantityInput: {
        css: css`
            background: transparent;
            border: none;
            text-align: center;
            padding: 0 4px;
            height: 100%;
            min-width: 44px;
            width: 100%;
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-weight: 600;
            font-size: 14px;
            color: ${getColor("text.main")};
            box-sizing: border-box;
            outline: none;
            flex: 1;
            height: 44px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            max-width: 44px;
                        `,
                    ],
                    "max",
                )}

            /* Remove input spinners */
            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }

            /* Firefox */
            &[type="number"] {
                -moz-appearance: textfield;
            }

            &:focus {
                outline: none;
                box-shadow: none;
                border: none;
                background: transparent;
            }

            &:disabled {
                background: transparent;
                color: ${getColor("text.main")};
                opacity: 0.7;
            }
        `,
    },
    modalContainer: {
        size: 550,
        cssOverrides: {
            modalTitle: css`
                border: 0;
                padding: 10px 20px;
                background-color: #f8f8f8;
            `,
            modalContent: css`
                padding: 20px 25px;
            `,
            modalBody: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 95%;
                            height: auto;
                        `,
                        css`
                            width: 75%;
                            height: auto;
                        `,
                        css`
                            width: 75%;
                            height: auto;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },
};

const CustomQuantityInput: FC<OwnProps> = ({
    cartLine,
    cart,
    canModifyOrder,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    label,
}) => {
    const [showQtyExceededModal, setShowQtyExceededModal] = useState(false);
    const [currentValue, setCurrentValue] = useState(cartLine.qtyOrdered!.toString());

    const exceedsLinkedRugQuantity = (proposedQty: number) => {
        const linkedRugItemCode = cartLine.properties?.linkedRugItemCode;
        if (cartLine.properties?.isRollpadService !== "true" || !linkedRugItemCode) {
            return false;
        }

        const linkedRug = cart.cartLines?.find(line => line.productName === linkedRugItemCode);
        if (!linkedRug) {
            return false;
        }

        const otherLinkedRugPadQty =
            cart.cartLines
                ?.filter(
                    line => line.id !== cartLine.id && line.properties?.isRollpadService === "true" && line.properties?.linkedRugItemCode === linkedRugItemCode,
                )
                .reduce((sum, line) => sum + (line.qtyOrdered ?? 0), 0) ?? 0;

        return otherLinkedRugPadQty + proposedQty > (linkedRug.qtyOrdered ?? 0);
    };

    const localOnQtyOrderedChange = (value: string, onError?: () => void) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            onError?.();
            return;
        }

        if (exceedsLinkedRugQuantity(inputQty)) {
            setCurrentValue(cartLine.qtyOrdered!.toString());
            setShowQtyExceededModal(true);
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(cartLine.qtyOrdered!.toString(), localOnQtyOrderedChange, cartLoadedTime);

    useEffect(() => {
        const value = cartLine.qtyOrdered!.toString();
        if (value !== currentValue) {
            setCurrentValue(value);
        }
    }, [cartLine.qtyOrdered]);

    const styles = { ...customQuantityInputStyles, ...extendedStyles };
    const modalContainerStyles = isMgbwWebsite()
        ? {
              ...styles.modalContainer,
              cssOverrides: {
                  ...styles.modalContainer?.cssOverrides,
                  modalTitle: css`
                      ${styles.modalContainer?.cssOverrides?.modalTitle}

                      h4 {
                          font-size: 20px;
                      }
                  `,
              },
          }
        : styles.modalContainer;

    const isDisabled = !canModifyOrder || cartLine.isPromotionItem || isLoading;
    const currentQty = parseFloat(currentValue) || 0;

    const isRugPad = cartLine.properties?.isRollpadService === "true";
    const hasLinkedRug = isRugPad ? cart.cartLines?.find(line => line.productName === cartLine.properties?.linkedRugItemCode) : null;
    const linkedRugQty = hasLinkedRug?.qtyOrdered ?? 0;
    const hasReachedLinkedQty = isRugPad && currentQty >= linkedRugQty;

    const handleDecrease = () => {
        if (isDisabled || currentQty <= 1) {
            return;
        }
        const newQty = Math.max(1, currentQty - 1);
        setCurrentValue(newQty.toString());
        onQtyOrderedChange(newQty);
    };

    const handleIncrease = () => {
        if (isDisabled) {
            return;
        }
        if (hasReachedLinkedQty) {
            setShowQtyExceededModal(true);
            return;
        }
        const newQty = Math.min(999999, currentQty + 1);
        if (exceedsLinkedRugQuantity(newQty)) {
            setShowQtyExceededModal(true);
            return;
        }
        setCurrentValue(newQty.toString());
        onQtyOrderedChange(newQty);
    };

    return (
        <StyledWrapper {...styles.qtyContainer}>
            <Clickable {...styles.decreaseButtonStyle} disabled={isDisabled || currentQty <= 1} onClick={handleDecrease} data-test-selector="quantity_decrease">
                <QtyMinusIcon />
            </Clickable>

            <StyledWrapper
                {...styles.quantityInput}
                as="input"
                type="number"
                min={1}
                max={999999}
                value={currentValue}
                disabled={isDisabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCurrentValue(e.target.value);
                    changeHandler(e);
                }}
                onKeyDown={keyDownHandler}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    blurHandler();
                    const inputQty = parseFloat(e.target.value);
                    if (!Number.isNaN(inputQty) && inputQty > 0 && !exceedsLinkedRugQuantity(inputQty)) {
                        setCurrentValue(inputQty.toString());
                    }
                }}
                onClick={(event: React.MouseEvent<HTMLInputElement>) => event.currentTarget.focus()}
                data-test-selector="cartline_qty_input"
            />

            <Clickable {...styles.increaseButtonStyle} disabled={isDisabled} onClick={handleIncrease} data-test-selector="quantity_increase">
                <QtyPlusIcon />
            </Clickable>

            <Modal
                {...modalContainerStyles}
                isOpen={showQtyExceededModal}
                headline="Quantity Limit"
                sizeVariant="small"
                isCloseable={true}
                handleClose={() => setShowQtyExceededModal(false)}
            >
                {siteMessage("LinkedQuantityMsg")}
            </Modal>
        </StyledWrapper>
    );
};

export default CustomQuantityInput;
